import classNames from 'classnames';
import Image from 'next/image';
import { type ReactNode, useContext } from 'react';
import { useIntl } from 'react-intl';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import Typography from '@alltrails/core/components/Typography';
import getPhotoUrl from '@alltrails/core/utils/getPhotoUrl';
import getStaticMapUrl from '@alltrails/core/utils/getStaticMapUrl';
import Heading from '@alltrails/denali/components/Heading';
import IconRenderer, { type IconDefinition } from '@alltrails/denali/components/IconRenderer';
import { ModalAriaLabelledByContext } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import type TrailDetails from '@alltrails/types/TrailDetails';
import styles from './FeatureContext.module.scss';

type FeatureContextProps = {
  featureIcon: IconDefinition<'size'>;
  trailDetails: TrailDetails;
  description?: ReactNode;
  heading?: ReactNode;
  isTallSheet?: boolean;
};

export default function FeatureContext({ description, featureIcon, heading, isTallSheet, trailDetails }: FeatureContextProps) {
  const ariaLabelledBy = useContext(ModalAriaLabelledByContext);
  const intl = useIntl();
  const publicBaseUrl = usePublicBaseUrl();

  const defaultPhotoUrl = trailDetails.defaultPhoto
    ? getPhotoUrl({
        isOptimized: true,
        photo: {
          id: trailDetails.defaultPhoto.id,
          photoHash: trailDetails.defaultPhoto.photoHash
        },
        size: '128x128'
      })
    : getStaticMapUrl({
        baseUrl: publicBaseUrl,
        id: trailDetails.verifiedMapId,
        scale: 1,
        size: '128x128',
        type: 'maps'
      });

  const staticMapUrl = getStaticMapUrl({
    baseUrl: publicBaseUrl,
    id: trailDetails.verifiedMapId,
    scale: 1,
    size: '128x128',
    type: 'maps'
  });

  return (
    <>
      <div className={styles.images}>
        <Image className={styles.rotatedImg} src={defaultPhotoUrl} alt="" width={64} height={64} />
        <Image className={styles.topImg} src={staticMapUrl} alt="" width={64} height={64} />
        <span className={styles.icon}>
          <IconRenderer icon={featureIcon} defaults={{ size: 'xs' }} />
        </span>
      </div>
      <Heading as="h2" id={ariaLabelledBy} size="400">
        {heading ?? intl.formatMessage({ defaultMessage: 'Stay on route with the app' })}
      </Heading>
      <Typography className={classNames(styles.description, isTallSheet && styles.tallDescription)} variant="text200">
        {description ??
          intl.formatMessage({
            defaultMessage: 'Use the AllTrails app to stay on route and follow the trail.'
          })}
      </Typography>
    </>
  );
}
